import { Injectable } from '@angular/core';
import { GradeDocument } from '../../models/grade.models';

@Injectable({
  providedIn: 'root'
})
export class CsvExportService {

  constructor() { }

  /**
   * Exports selected grade documents to CSV format and triggers download
   * @param documents Array of GradeDocument objects to export
   * @param filename Name of the file to download (without extension)
   */
  exportGradeDocumentsToCsv(documents: GradeDocument[], filename: string = 'grade-export'): void {
    if (!documents || documents.length === 0) {
      console.warn('No documents to export');
      return;
    }

    // Define the headers matching your requirements
    const headers = [
      'Authors',
      'Title',
      'Study Type',
      'Main Outcome',
      'Consistency',
      'Risk of Bias',
      'Grade Quality'
    ];

    // Transform documents to flat structure for CSV export
    const csvData = documents.map(doc => ({
      'Authors': doc.authors || '',
      'Title': doc.title || '',
      'Study Type': doc.study_type || '',
      'Main Outcome': doc.main_outcome || '',
      'Consistency': doc.consistency ? this.formatGradeQuality(doc.consistency) : '',
      'Risk of Bias': doc.risk_of_bias ? this.formatRiskOfBias(doc.risk_of_bias) : '',
      'Grade Quality': doc.grade_quality ? this.formatGradeQuality(doc.grade_quality) : ''
    }));

    this.exportToCsv(csvData, filename, headers);
  }

  /**
   * Exports an array of objects to CSV format and triggers download
   * @param data Array of objects to export
   * @param filename Name of the file to download
   * @param headers Optional array of column headers. If not provided, uses object keys
   */
  exportToCsv(data: any[], filename: string, headers?: string[]): void {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    const csvContent = this.convertToCsv(data, headers);
    this.downloadCsv(csvContent, filename);
  }

  /**
   * Converts array of objects to CSV string
   * @param data Array of objects to convert
   * @param headers Optional array of column headers
   * @returns CSV string
   */
  private convertToCsv(data: any[], headers?: string[]): string {
    if (!data || data.length === 0) {
      return '';
    }

    // Use provided headers or extract from first object
    const columnHeaders = headers || Object.keys(data[0]);

    // Create CSV header row
    const headerRow = columnHeaders.map(header => this.escapeCsvField(header)).join(',');

    // Create CSV data rows
    const dataRows = data.map(item => {
      return columnHeaders.map(header => {
        const value = item[header];
        return this.escapeCsvField(value);
      }).join(',');
    });

    return [headerRow, ...dataRows].join('\n');
  }

  /**
   * Escapes a field for CSV format
   * @param field Field value to escape
   * @returns Escaped field value
   */
  private escapeCsvField(field: any): string {
    if (field === null || field === undefined) {
      return '';
    }

    let stringField = String(field);

    // If field contains comma, double quote, or newline, wrap in quotes
    if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
      // Escape existing double quotes by doubling them
      stringField = stringField.replace(/"/g, '""');
      stringField = `"${stringField}"`;
    }

    return stringField;
  }

  /**
   * Triggers download of CSV content
   * @param csvContent CSV string content
   * @param filename Filename for download
   */
  private downloadCsv(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', this.ensureCsvExtension(filename));
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Ensures filename has .csv extension
   * @param filename Original filename
   * @returns Filename with .csv extension
   */
  private ensureCsvExtension(filename: string): string {
    return filename.endsWith('.csv') ? filename : `${filename}.csv`;
  }

  /**
   * Formats grade quality values to human-readable format
   * @param quality Grade quality value
   * @returns Formatted string
   */
  private formatGradeQuality(quality: string): string {
    return quality.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Formats risk of bias values to human-readable format
   * @param risk Risk of bias value
   * @returns Formatted string
   */
  private formatRiskOfBias(risk: string): string {
    return risk.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
}